<script setup>
import { useUiStore } from '@/stores/UiStore'
import { useToastStore } from '@/stores/Toast'
import { useDriverStore } from '@/stores/Drivers'
import { UPDATE_DRIVER } from '@/constants/dispatch'
import { computed, ref, watch, onMounted } from 'vue'
import { useLoadSegmentsStore } from '@/stores/LoadSegments'

/**
 * Constant for unassigned driver id.
 *
 * @type {Number}
 */
const UNASSIGNED_DRIVER_ID = -1

/**
 * The store instances.
 */
const uiStore = useUiStore()
const toastStore = useToastStore()
const driverStore = useDriverStore()
const segmentsStore = useLoadSegmentsStore()

/**
 * The component's local refs.
 */
const isLoading = ref(false)
const selectedDriver = ref(null)

/**
 * The load's data.
 *
 * @return {Object}
 */
const data = computed(() => {
    return uiStore.actionLoads(UPDATE_DRIVER)
})

/**
 * The load's driver.
 *
 * @return {Object}
 */
const driver = computed(() => {
    const activeSegmentActionId = uiStore.actionSegmentId(UPDATE_DRIVER)

    if (activeSegmentActionId) {
        return segmentsStore.find(activeSegmentActionId).drivers[0]
    }

    if (!data.value?.drivers?.length) {
        return null
    }

    return data.value.drivers[0]
})

/**
 * The load's drivers.
 *
 * @return {Array}
 */
const drivers = computed(() => {
    const defaultDriver = {
        id: UNASSIGNED_DRIVER_ID,
        name: 'Unassign driver',
    }

    return [...[defaultDriver], ...driverStore.drivers]
})

/**
 * Determine if the action should be shown.
 *
 * @return {Boolean}
 */
const show = computed(() => {
    return uiStore.isActionActive(UPDATE_DRIVER)
})

/**
 * The on accept handler.
 *
 * @return {Promise}
 */
const onAccept = async () => {
    try {
        isLoading.value = true
        const { id, current_load_segment_id } = data.value
        const activeSegmentActionId = uiStore.actionSegmentId(UPDATE_DRIVER)

        const segmentId = activeSegmentActionId
            ? activeSegmentActionId
            : current_load_segment_id

        await segmentsStore.updateSegment(
            segmentId,
            {
                driver_ids:
                    selectedDriver.value !== UNASSIGNED_DRIVER_ID
                        ? [].concat(selectedDriver.value)
                        : false,
            },
            activeSegmentActionId ? null : id
        )

        toastStore.setNotification({
            message: `Load's driver updated successfully`,
            type: 'success',
        })
    } catch (error) {
        toastStore.setNotification({
            message: error.message(),
            type: 'error',
        })
    } finally {
        uiStore.setActionSegmentId(UPDATE_DRIVER, null)

        isLoading.value = false

        onCancel()
    }
}

/**
 * Handle the cancel click event.
 *
 * @return {void}
 */
const onCancel = () => {
    uiStore.closeAction(UPDATE_DRIVER)
}

/**
 * The component's on mounted lifecycle hook.
 *
 * @return {void}
 */
onMounted(async () => {
    if (show.value && data.value.id) {
        await segmentsStore.get(data.value.id)
    }
})

/**
 * Watch the driver for changes.
 */
watch(
    () => driver,
    (driver) => {
        if (!driver?.value) {
            return
        }

        selectedDriver.value = driver.value.id
    },
    {
        immediate: true,
    }
)
</script>

<template>
    <v-dialog
        data-test="confirmation-dialog"
        v-model="show"
        persistent
        width="480"
    >
        <v-card class="!rounded-lg">
            <v-card-item
                title="Change load's driver"
                density="compact"
                class="!py-2 !px-4 bg-gray-100 border-b"
            >
                <template v-slot:append>
                    <v-btn
                        size="x-small"
                        variant="text"
                        icon="fal fa-close"
                        color="primary"
                        class="rounded-md"
                        @click="onCancel"
                    >
                    </v-btn>
                </template>
            </v-card-item>
            <v-card-text class="!px-4" data-test="confirmation-dialog-body">
                <v-form>
                    <div class="text-base mb-5">
                        Please select a driver to assign to PRO #{{ data.id }}:
                    </div>
                    <v-autocomplete
                        class="mt-4"
                        v-model="selectedDriver"
                        label="Driver"
                        :items="drivers"
                        item-title="name"
                        item-value="id"
                        variant="outlined"
                        density="comfortable"
                    ></v-autocomplete>
                </v-form>
            </v-card-text>
            <div class="bg-gray-100 border-t py-2 px-4">
                <div class="text-xs">
                    Changing the driver of an active load may cause issues for
                    the current load segments and stops.
                </div>
            </div>
            <v-spacer class="border-b"></v-spacer>
            <v-card-actions class="px-4 justify-end">
                <v-btn
                    data-test="confirmation-dialog-btn-close"
                    variant="outlined"
                    @click="onCancel"
                >
                    Cancel
                </v-btn>
                <v-btn
                    :loading="isLoading"
                    :disabled="!selectedDriver"
                    color="primary"
                    variant="flat"
                    data-test="confirmation-dialog-btn-accept"
                    @click="onAccept"
                >
                    Save
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>
